/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {LocaleAdapterInterface} from '@/services/language/types';
import {Vue} from 'vue-property-decorator';
import {Translations} from 'vuex-i18n';

/**
 * I18n locale adapter.
 *
 * @implements LocaleAdapterInterface
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export class I18nAdapter implements LocaleAdapterInterface {
    /**
     * Language setter.
     *
     * @param locale
     */
    public async setLocale(locale: string): Promise<void> {
        // dynamically load if language doesn't exist
        if (!Vue.i18n.localeExists(locale)) {
            try {
                (null === Vue.i18n.locale())
                    ? this.initialImport(locale)
                    : await this.dynamicImport(locale);
            } catch (exception) {
                // do nothing
            }
        }

        if (Vue.i18n.localeExists(locale)) {
            Vue.i18n.set(locale);
        }
    }

    /**
     * Import locale using require methodology.
     *
     * This implementation works best on initial load of the the i18n library.
     *
     * @param locale
     */
    private initialImport(locale: string): void {
        Vue.i18n.add(locale, require(`@/i18n/${locale}.json`));
    }

    /**
     * Dynamic import of locale using import methodology.
     *
     * This asynchronous implementation works best when the library has already been initialised.
     *
     * @param locale
     */
    private async dynamicImport(locale: string): Promise<void> {
        try {
            const translationModule: any = await import(`@/i18n/${locale}.json`);
            const translation: Translations = {};
            Object.assign(translation, translationModule);
            Vue.i18n.add(locale, translation);
       } catch (exception) {
            // do nothing
        }

    }
}
